import { defineComponent, onMounted, onUnmounted, Ref, ref } from 'vue';
import { ITextArea } from '@ibiz/model-core';
import {
  useNamespace,
  getInputProps,
  getEditorEmits,
} from '@ibiz-template/vue3-util';
import { createUUID } from 'qx-util';
import { EmbedAIChatEditorController } from '../embed-ai-chat-editor.controller';
import './embed-ai-chat.scss';

/**
 * 移动端AI聊天嵌入编辑器
 *
 * @description 用于在页面嵌入AI聊天组件。基于`移动端文本框`编辑器进行扩展，编辑器样式代码名称为：EMBED_AI_CHAT
 * @primary
 * @editorparams {"name":"srfaiappendcurdata","parameterType":"boolean","defaultvalue":false,"description":"在打开AI功能时，该参数用于判断是否传入对象参数，主要用于在请求历史记录时，附加当前参数"}
 * @editorparams {"name":"srfaiappendcurcontent","parameterType":"string","description":"在打开AI功能时，如果该参数存在值，会将其传入编辑内容作为用户消息，主要用于在请求历史记录后，附加当前编辑内容作为用户消息"}
 * @editorparams {"name":"autoquestion","parameterType":"boolean","defaultvalue":true,"description": "在打开AI功能时历史数据最后一个项是用户消息（USER）时是否自动提问，当打开AI行内聊天时是否自动提问"}
 * @editorparams {"name":"autofill","parameterType":"boolean","defaultvalue":false,"description": "用于AI聊天，AI回答完成之后是否触发回填"}
 * @editorparams {"name":"autoclose","parameterType":"{mode:'minimize' | 'close' | 'closetime',duration?:number}","description": "用于AI聊天，在提问完成后，设置AI窗口的自动关闭模式。其中 mode 设为 minimize 时窗口会最小化，设为 close 时窗口会直接关闭，设为 closetime 时窗口会根据 duration 配置的值延时关闭。duration配置单位为秒（s），默认值为 3 秒"}
 * @editorparams {"name":"enablenoaccess","parameterType":"boolean","defaultvalue":"false", "description":"是否启用无权限模式，若启用无权限模式，上传文件夹需拼接'$'字符，也不需要计算下载凭证"}
 * @editorparams {"name":"globaldownloadprifix","parameterType":"boolean","defaultvalue":"false", "description":"是否使用全局文件下载前缀，若启用，则以global作为前缀"}
 * @editorparams {"name":"srfaiappendresource","parameterType":"string", "description":"AI聊天默认附加资源数据"}
 * @editorparams {"name":"srfmode","parameterType":"string", "description":"指定AI聊天自定义模式"}
 * @editorparams {"name":"srfaichunkview","parameterType":"string", "description":"知识切片视图，用于定义AI交谈打开目标知识切片视图"}
 * @editorparams {"name":"srfaichunkentity","parameterType":"string", "description":"知识切片实体，用于定义AI交谈打开知识切片视图数据主键key"}
 * @editorparams {"name":"chunkview","parameterType":"string", "description":"切片视图，用于定义打开目标切片视图"}
 * @editorparams {"name":"chunkentity","parameterType":"string", "description":"切片实体，用于打开切片视图数据主键key"}
 * @editorparams {"name":"openmode",parameterType:'default' | 'top' | 'bottom',description: 用于AI聊天，AI窗口的打开模式，default：默认窗口模式；top：顶部飘窗模式；bottom：底部飘窗模式}
 * @editorparams {"name":"containerheight",parameterType:"number",description: 用于AI聊天飘窗模式时的高度，0到100按照百分比，大于100按照像素处理}
 * @editorparams {"name":"srfaichatcaption","parameterType":"string", "description":"自定义AI交谈框标题"}
 * @editorparams {"name":"srfaitopiccaption","parameterType":"string", "description":"自定义AI话题标题"}
 * @ignoreprops overflowMode
 * @ignoreemits infoTextChange
 */
export const IBizEmbedAIChat = defineComponent({
  name: 'IBizEmbedAIChat',
  props: getInputProps<EmbedAIChatEditorController>(),
  emits: getEditorEmits(),
  setup(props) {
    const ns = useNamespace('embed-ai-chat');
    const c = props.controller;
    // AI聊天框挂载元素
    const aiChatElementRef: Ref<HTMLElement | null> = ref(null);
    // 挂载AI聊天框
    const mountedAIChat = async () => {
      const appDataEntityId = (c.model as ITextArea).appDataEntityId;
      const chatInstance = c.chatInstance;
      if (!appDataEntityId || !c.deACMode || !chatInstance) return;
      const topicId = createUUID();
      const sessionid = ibiz.aiChatUtil.getChatSessionId('TOPIC', topicId);
      const { containerOptions, chatOptions, topicOptions } =
        await ibiz.aiChatUtil.getEditorExAIChatParams(
          c.editorParams,
          c.context,
          c.params,
          props.data,
          c.deACMode,
          { chatInstance, view: c.view, ctrl: c.ctrl },
        );
      const resourceOptions = await ibiz.aiChatUtil.getAIResourceOptions(
        c.context,
        c.params,
      );
      chatInstance.create({
        mode: 'TOPIC',
        resourceOptions,
        containerOptions: {
          ...containerOptions,
          container: aiChatElementRef.value,
        },
        topicOptions: {
          appid: ibiz.env.appId,
          id: topicId,
          url: window.location.hash.substring(1),
          type: c.context.srftopicpath || 'default',
          ...topicOptions,
        },
        chatOptions: {
          context: { ...c.context },
          params: { ...c.params, ...{ srfactag: c.deACMode.codeName } },
          appDataEntityId,
          // 扩展参数
          ...chatOptions,
          sessionid,
        },
      });
    };

    onMounted(() => {
      mountedAIChat();
    });

    onUnmounted(() => {
      const chatInstance = c.chatInstance;
      if (chatInstance) {
        chatInstance.close();
      }
    });

    return {
      c,
      ns,
      aiChatElementRef,
    };
  },
  render() {
    return (
      <div class={this.ns.b()}>
        <div class={this.ns.e('container')} ref='aiChatElementRef'></div>
      </div>
    );
  },
});
